import { carrierRebrandMap } from '~/constants/carriers';
import { currencies } from '~/constants/currency';
import * as i18n from 'i18next';
import { uniqBy } from 'lodash-es';
import differenceBy from 'lodash-es/differenceBy';
import intersectionBy from 'lodash-es/intersectionBy';
import * as moment from 'moment';
import { ReturnRate } from '~/pages/Batch/Rates/LabelsTotal/LabelsList';
import { BatchEntryExtended } from '~/types/OrderBatchEntry';
import ServiceLevel, {
  fromJSONServiceLevelTerms,
  fromWebRatesServiceLevel,
  isOneRate,
  JSONServiceLevel,
  ServiceLevelCategoryByToken,
  serviceLevelMatchesToken,
  WebRatesServiceLevel,
} from '~/types/ServiceLevel';
import { TranslationFunction } from '~/utils/translate';

enum RateAttribute {
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  BESTVALUE = 'BESTVALUE',
  POPULAR = 'POPULAR',
}

export const rateDisplayAttribute = {
  CHEAPEST: 'CHEAPEST',
  POPULAR: 'POPULAR',
  FASTEST: 'FASTEST',
  CHEAPEST_AND_FASTEST: 'CHEAPEST_AND_FASTEST',
  MORE: 'MORE',
} as const;

export type RateDisplayAttribute = (typeof rateDisplayAttribute)[keyof typeof rateDisplayAttribute];

export const serviceLevelToken = {
  USPS_PRIORITY: 'usps_priority',
  USPS_FIRST_CLASS: 'usps_first_class',
  USPS_PRIORITY_EXPRESS: 'usps_priority_express',
  USPS_PARCEL_SELECT: 'usps_parcel_select',
  UPS_GROUND: 'ups_ground',
  USPS_GROUND_ADVANTAGE: 'usps_ground_advantage',
  FEDEX_GROUND_ECONOMY: 'fedex_ground_economy',
  UPS_GROUND_SAVER: 'ups_ground_saver',
} as const;

export type ServiceLevelToken = typeof serviceLevelToken;

export const popularServices = [serviceLevelToken.UPS_GROUND, serviceLevelToken.USPS_PRIORITY];

export type RateObjectId = string;

type BaseRate = {
  amount: string;
  currency: string;
  durationTerms: string;
  objectId: RateObjectId;
  objectPurpose?: string;
  objectState?: string;
  provider: string;
  providerImage75: string;
  providerImage200: string;
  shipment: string;
};

type JSONBaseRate = {
  amount: string;
  currency: string;
  duration_terms: string;
  object_id: string;
  object_purpose: string;
  object_state: string;
  provider: string;
  provider_image_75: string;
  provider_image_200: string;
  shipment: string;
};

function fromJSONBaseRate(jsonBaseRate: JSONBaseRate): BaseRate {
  return {
    amount: jsonBaseRate.amount,
    currency: jsonBaseRate.currency,
    durationTerms: jsonBaseRate.duration_terms,
    objectPurpose: jsonBaseRate.object_purpose,
    objectId: jsonBaseRate.object_id,
    objectState: jsonBaseRate.object_state,
    provider: jsonBaseRate.provider,
    providerImage75: jsonBaseRate.provider_image_75,
    providerImage200: jsonBaseRate.provider_image_200,
    shipment: jsonBaseRate.shipment,
  };
}

type JSONRate = {
  object_created: string;
  object_id: string;
  object_owner: string;
  shipment: string;
  attributes: string[];
  amount: string;
  currency: string;
  amount_local: string;
  currency_local: string;
  provider: string;
  provider_image_75: string;
  provider_image_200: string;
  servicelevel: JSONServiceLevel;
  servicelevel_name?: string;
  servicelevel_token?: string;
  servicelevel_terms?: string;
  estimated_days: number;
  arrives_by: string;
  insurance: boolean;
  duration_terms: string;
  messages: string[];
  carrier_account: string;
  test: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zone: any;
  included_insurance_price?: string;
};

interface Rate extends BaseRate {
  objectCreated: moment.Moment;
  objectOwner: string;
  attributes: RateAttribute[];
  amountLocal: string;
  currencyLocal: string;
  servicelevel: ServiceLevel;
  servicelevelName?: string;
  estimatedDays: number;
  arrivesBy: string;
  insurance: boolean;
  durationTerms: string;
  messages: string[];
  carrierAccount: string;
  test: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zone: any;
  includedInsurancePrice?: string;
}

export type WebRate = {
  amount: string;
  amount_local: string;
  arrives_by: string;
  attributes: string[];
  carrier_account: string;
  currency: string;
  currency_local: string;
  duration_terms: string;
  estimated_days: number;
  included_insurance_price: string;
  messages: string[];
  object_created: Date;
  object_id: string;
  object_owner: string;
  provider: string;
  provider_image_200: string;
  provider_image_75: string;
  servicelevel: WebRatesServiceLevel;
  shipment: string;
  test: boolean;
  zone: string;
};

type RateLabel = {
  servicelevel: ServiceLevel;
  provider: string;
};

function fromJSON(jsonRate: JSONRate): Rate {
  // transactions.rates and shipments.rates have different response shape for servicelevel
  // this handles shipments.rates.servicelevel vs transactions.rates.servicelevel_*
  let jsonRateServiceLevel = jsonRate.servicelevel;
  if (!jsonRate.servicelevel) {
    jsonRateServiceLevel = {
      name: jsonRate.servicelevel_name || '',
      token: jsonRate.servicelevel_token || '',
      terms: jsonRate.servicelevel_terms || '',
    };
  }
  return {
    objectCreated: moment(jsonRate.object_created),
    objectId: jsonRate.object_id,
    objectOwner: jsonRate.object_owner,
    shipment: jsonRate.shipment,
    attributes: jsonRate.attributes.map((attr) => <RateAttribute>attr),
    amount: jsonRate.amount,
    currency: jsonRate.currency,
    amountLocal: jsonRate.amount_local,
    currencyLocal: jsonRate.currency_local,
    provider: jsonRate.provider,
    providerImage75: jsonRate.provider_image_75,
    providerImage200: jsonRate.provider_image_200,
    servicelevel: {
      ...jsonRateServiceLevel,
      terms: fromJSONServiceLevelTerms(jsonRateServiceLevel.terms, jsonRate.provider),
    },
    servicelevelName: jsonRate.servicelevel_name || '',
    estimatedDays: jsonRate.estimated_days,
    arrivesBy: jsonRate.arrives_by,
    insurance: jsonRate.insurance,
    durationTerms: jsonRate.duration_terms,
    messages: jsonRate.messages,
    carrierAccount: jsonRate.carrier_account,
    test: jsonRate.test,
    zone: jsonRate.zone,
    includedInsurancePrice: jsonRate.included_insurance_price,
  };
}

export function fromWebRate(webRate: WebRate): Rate {
  const attributes = webRate.attributes.map((t) => <RateAttribute>t);
  const servicelevel = fromWebRatesServiceLevel(webRate.servicelevel);
  return {
    amount: webRate.amount,
    currency: webRate.currency,
    durationTerms: webRate.duration_terms,
    objectId: webRate.object_id,
    provider: webRate.provider,
    providerImage75: webRate.provider_image_75,
    providerImage200: webRate.provider_image_200,
    shipment: webRate.shipment,
    objectCreated: moment(webRate.object_created),
    objectOwner: webRate.object_owner,
    amountLocal: webRate.amount_local,
    currencyLocal: webRate.currency_local,
    servicelevelName: webRate.servicelevel?.name,
    estimatedDays: webRate.estimated_days,
    arrivesBy: webRate.arrives_by,
    messages: webRate.messages,
    test: webRate.test,
    carrierAccount: webRate.carrier_account,
    zone: webRate.zone,
    includedInsurancePrice: webRate.included_insurance_price,

    servicelevel,
    attributes,

    //nulls,
    objectState: undefined,
    objectPurpose: undefined,

    // Not sure where this value would come from or when it would be true
    insurance: false,
  };
}

function getLabel(rate: RateLabel): string {
  // Edge cases where label does not follow "<provider> <service level name>" format
  // See tech debt ticket: https://shippo.atlassian.net/browse/ENG-13200
  // Ticket proposes updating /shipments endpoint to return rates with a servicelevel label field
  // so frontend does not need to know how to compile the service level label
  const serviceLevelNamesToCheck = ['Ground', 'Ground Saver'];
  if (rate.provider === 'UPS' && serviceLevelNamesToCheck.includes(rate.servicelevel.name)) {
    // UPS Ground needs to look like UPS® Ground
    return `${rate.provider}® ${rate.servicelevel.name}`;
  }

  // https://shippo.atlassian.net/browse/ENG-29904
  if (carrierRebrandMap[rate.provider]) {
    return `${carrierRebrandMap[rate.provider]} ${rate.servicelevel.name}`;
  }

  return `${rate.provider} ${rate.servicelevel.name}`;
}

function getLabelWithOneRate(rate: RateLabel): string {
  const { servicelevel } = rate;
  const oneRateText = isOneRate(servicelevel) ? ' One Rate' : '';
  return `${getLabel(rate)}${oneRateText}`;
}

function optionFromRate(t: TranslationFunction) {
  return (rate: Rate) => {
    const price = t('currency', { value: { value: rate.amount, currency_iso: rate.currency } });

    return {
      value: rate.objectId,
      label: `${getLabel(rate)} ${isOneRate(rate.servicelevel) ? '- One Rate' : ''} (${price}) ${
        rate.test ? '- Test' : ''
      }`,
    };
  };
}

export const addAttributesToRateMap = (attributes: RateAttribute[]) => {
  return (rate: Rate) => ({
    ...rate,
    attributes: attributes,
  });
};

export const popularServicesV2 = [
  serviceLevelToken.USPS_PRIORITY,
  serviceLevelToken.UPS_GROUND,
  serviceLevelToken.UPS_GROUND_SAVER,
];
/**
 * This function adds popular attribute to rate based on the new logic
 * https://shippo.atlassian.net/browse/ENG-41605
 * Show these services as popular, sorted by cheapest, removing the cheapest if it is among these services to avoid duplication in the cheapest tag:
 * USPS Priority Mail
 * FedEx Ground Economy
 * UPS Ground
 * UPS Ground Saver
 *
 * TODO: Move this logic to the backend
 * @param rates the list of rates for a shipment
 * @returns the same list of rates but with one rate potentially attributed as POPULAR based on business logic
 */
export function attributePopularRateV2(rates: Rate[]): Rate[] {
  // Find the cheapest rates
  const cheapestRates = rates.filter((r: Rate) => r.attributes.includes(RateAttribute.CHEAPEST));

  // Find all the other rates than cheapest rates
  const otherThanCheapestRates = differenceBy(rates, cheapestRates, (r: Rate) => r.objectId);

  // Find all the popular rates and sort them by price
  const popularRates = otherThanCheapestRates
    .filter((r: Rate) =>
      popularServicesV2.includes(r.servicelevel.token as (typeof popularServicesV2)[number]),
    )
    .sort((a, b) => sortBy(a, b, 'cheapest'));

  // Get all the other rates than popular rates and cheapest rates
  const otherRates = differenceBy(
    rates,
    cheapestRates.concat(popularRates),
    (r: Rate) => r.objectId,
  );

  // Adding POPULAR to attribute of each popular rate for tracking
  const updatedPopularRatesList = popularRates.map(addAttributesToRateMap([RateAttribute.POPULAR]));

  return uniqBy(
    updatedPopularRatesList.concat(cheapestRates).concat(otherRates),
    (rate: Rate) => rate.objectId,
  );
}

// getNextCheapestRates checks if there are no cheapest and/or fastest rates, and selects
// the next rate with an amount from the "Other rates" input, which should be already
// sorted by price ascending (from cheapest to most expensive)
function getNextCheapestRate({
  sortedOtherRates,
  cheapestAndFastestRates,
  cheapestRates,
}: {
  sortedOtherRates: Rate[];
  cheapestAndFastestRates: Rate[];
  cheapestRates: Rate[];
}) {
  if (cheapestAndFastestRates.length === 0 && cheapestRates.length === 0) {
    const firstRateWithAmountIndex = sortedOtherRates.findIndex((rate) => Number(rate.amount));

    if (firstRateWithAmountIndex > -1) {
      const firstRateWithAmount = sortedOtherRates[firstRateWithAmountIndex];
      sortedOtherRates.splice(firstRateWithAmountIndex, 1);
      return {
        updatedCheapestRatesList: [firstRateWithAmount],
        updatedOtherRatesList: sortedOtherRates,
      };
    }

    return {
      updatedCheapestRatesList: [],
      updatedOtherRatesList: sortedOtherRates,
    };
  }

  return {
    updatedCheapestRatesList: cheapestRates,
    updatedOtherRatesList: sortedOtherRates,
  };
}

function getRatesByAttribute(rates: Rate[]) {
  const cheapestRates = rates.filter((r: Rate) => r.attributes.includes(RateAttribute.CHEAPEST));
  const fastestRates = rates.filter((r: Rate) => r.attributes?.includes(RateAttribute.FASTEST));
  const popularRates = rates.filter((r: Rate) => r.attributes?.includes(RateAttribute.POPULAR));

  // Note that "Best Value" rates aren't currently being shown separately; they're just being
  // included in "other rates"

  const otherRates = differenceBy(
    rates,
    cheapestRates.concat(fastestRates).concat(popularRates),
    (r: Rate) => r.objectId,
  ).sort((a, b) => sortBy(a, b, 'cheapest'));

  const cheapestAndFastest: Rate[] = intersectionBy(
    cheapestRates,
    fastestRates,
    (r: Rate) => r.objectId,
  );

  const onlyCheapestRates = differenceBy(
    cheapestRates,
    cheapestAndFastest,
    (r: Rate) => r.objectId,
  ).sort((a, b) => sortBy(a, b, 'fastest'));

  const { updatedCheapestRatesList, updatedOtherRatesList } = getNextCheapestRate({
    sortedOtherRates: otherRates,
    cheapestAndFastestRates: cheapestAndFastest,
    cheapestRates: onlyCheapestRates,
  });

  return {
    cheapestRates: updatedCheapestRatesList,
    fastestRates: differenceBy(fastestRates, cheapestAndFastest, (r: Rate) => r.objectId).sort(
      (a, b) => sortBy(a, b, 'cheapest'),
    ),
    cheapestAndFastest,
    otherRates: updatedOtherRatesList,
    popularRates,
  };
}

/**
 * This function should return an array of rates and display attribute
 * The order of the array is important. This is the same order the rates will be displayed
 * The order is
 * 1. Cheapest
 * 2. Popular
 * 3. Fastest
 * 4. Cheapest and Fastest
 * 5. More
 * @param rates the list of rates for a shipment
 * @returns [{
 *   displayAttribute: RateDisplayAttribute;
 *   rates: Rate[];
 * }]
 */
function getRatesWithAttributeInDisplayOrder(rates: Rate[]): {
  displayAttribute: RateDisplayAttribute;
  rates: Rate[];
}[] {
  const cheapestRates = rates.filter((r: Rate) => r.attributes?.includes(RateAttribute.CHEAPEST));
  const fastestRates = rates.filter((r: Rate) => r.attributes?.includes(RateAttribute.FASTEST));
  const popularRates = rates.filter((r: Rate) => r.attributes?.includes(RateAttribute.POPULAR));

  // Get all the other rates than popular rates, cheapest rates and fastest rates
  const otherRates = differenceBy(
    rates,
    cheapestRates.concat(fastestRates).concat(popularRates),
    (r: Rate) => r.objectId,
  ).sort((a, b) => sortBy(a, b, 'cheapest'));

  const cheapestAndFastest: Rate[] = intersectionBy(
    cheapestRates,
    fastestRates,
    (r: Rate) => r.objectId,
  );

  const onlyCheapestRates = differenceBy(
    cheapestRates,
    cheapestAndFastest,
    (r: Rate) => r.objectId,
  ).sort((a, b) => sortBy(a, b, 'fastest'));

  const { updatedCheapestRatesList, updatedOtherRatesList } = getNextCheapestRate({
    sortedOtherRates: otherRates,
    cheapestAndFastestRates: cheapestAndFastest,
    cheapestRates: onlyCheapestRates,
  });

  // remove fastest and cheapest rates from fastest rates
  const fastestRatesNotInCheapestAndFastest = differenceBy(
    fastestRates,
    cheapestAndFastest,
    (r: Rate) => r.objectId,
  ).sort((a, b) => sortBy(a, b, 'cheapest'));

  return [
    {
      displayAttribute: rateDisplayAttribute.CHEAPEST,
      rates: updatedCheapestRatesList,
    },
    {
      displayAttribute: rateDisplayAttribute.POPULAR,
      rates: popularRates,
    },
    {
      displayAttribute: rateDisplayAttribute.FASTEST,
      rates: fastestRatesNotInCheapestAndFastest,
    },
    {
      displayAttribute: rateDisplayAttribute.CHEAPEST_AND_FASTEST,
      rates: cheapestAndFastest,
    },
    {
      displayAttribute: rateDisplayAttribute.MORE,
      rates: updatedOtherRatesList,
    },
  ];
}

function getRatesForServiceLevelToken(
  rates: Rate[],
  serviceLevelToken: string,
  preferredAccountObjectID?: string,
): Rate[] {
  // if there are no rates provided, don't bother searching them
  if (rates.length < 1) {
    return rates;
  }

  // check if the service level token is a category, such as "CHEAPEST" - and search for Rates with that property
  const serviceLevelCategory = ServiceLevelCategoryByToken[serviceLevelToken]?.token?.toUpperCase();
  if (serviceLevelCategory) {
    const { cheapestRates, fastestRates } = getRatesByAttribute(rates);
    if (serviceLevelCategory === 'CHEAPEST' && cheapestRates.length > 0) {
      return cheapestRates;
    } else if (serviceLevelCategory === 'FASTEST' && fastestRates.length > 0) {
      return fastestRates;
    }
  }

  // otherwise, check if the rates have matching service level tokens
  return rates.filter((rate) => {
    // we can disqualify this rate if a Preferred Carrier Account was specified and it doesn't match the Rate's Carrier
    if (preferredAccountObjectID && rate.carrierAccount !== preferredAccountObjectID) {
      return false;
    }
    return serviceLevelMatchesToken(rate.servicelevel, serviceLevelToken);
  });
}

interface RateTotal {
  rate: { provider: string; servicelevel: ServiceLevel };
  amount: number;
  currency: string;
  total: number;
  totalInsuranceAmount: number;
  returnAmount?: number;
  shipmentObjectId?: string;
  includedInsurancePrice?: number;
  signatureConfirmation?: {
    hasSignatureConfirmation: boolean;
    signatureConfirmationType:
      | 'STANDARD'
      | 'ADULT'
      | 'CERTIFIED'
      | 'INDIRECT'
      | 'CARRIER_CONFIRMATION';
  };
  numberWithInsurance?: number;
  numberWithSignature?: number;
}

function createRateList(
  selectedRatesForValidOrders: string[],
  ratesData: HashMap<string, Rate>,
  validBatchEntries?: BatchEntryExtended[],
): RateTotal[] {
  const selectedRates = selectedRatesForValidOrders.map((selectedRate) => ratesData[selectedRate!]);

  // We need to group rates to show count of each type and how much is the cost
  return selectedRates.reduce((list: RateTotal[], selectedRate) => {
    // First we search to see if the rate is already in our list that we are creating
    if (!selectedRate) return list;

    const rateIndex = list.findIndex(({ rate }: RateTotal) => {
      return (
        rate.provider === selectedRate.provider &&
        rate.servicelevel.token === selectedRate.servicelevel.token
      );
    });

    // this is a temporary solution to pull insurance for rate selection that avoids an API change
    // the Rate object has an amount_insurance_fee field that can be used here
    // todo https://shippo.atlassian.net/browse/ENG-25100
    const batchEntry = validBatchEntries?.find(({ shipmentObjectId }) => {
      return selectedRate.shipment === shipmentObjectId;
    });

    const insuranceAmount = batchEntry?.extras?.insurance?.amount || 0;
    const isReturnRequested = batchEntry?.extras?.isReturnRequested;

    const signatureConfirmation = batchEntry?.extras?.signatureConfirmation;

    // If we found it, we are going to update it adding up to the amount and the total price
    if (rateIndex !== -1) {
      return [
        ...list.slice(0, rateIndex),
        {
          ...list[rateIndex],
          amount: list[rateIndex].amount + 1,
          currency: selectedRate.currency,
          total: list[rateIndex].total + Number(selectedRate.amount),
          totalInsuranceAmount: list[rateIndex].totalInsuranceAmount + Number(insuranceAmount),
          includedInsurancePrice:
            Number(list[rateIndex].includedInsurancePrice || null) +
            Number(selectedRate?.includedInsurancePrice || null),
          signatureConfirmation,
          returnAmount:
            list[rateIndex].returnAmount &&
            (list[rateIndex].returnAmount as number) + (isReturnRequested ? 1 : 0),
          numberWithInsurance:
            Number(insuranceAmount) > 0
              ? (list[rateIndex].numberWithInsurance || 0) + 1
              : list[rateIndex].numberWithInsurance || 0,
          numberWithSignature: signatureConfirmation?.hasSignatureConfirmation
            ? (list[rateIndex].numberWithSignature || 0) + 1
            : list[rateIndex].numberWithSignature || 0,
        },
        ...list.slice(rateIndex + 1),
      ] as RateTotal[];
    }

    // If we didn't found any we will add it to our list with the amount and total for just that rate
    return [
      ...list,
      {
        rate: { provider: selectedRate.provider, servicelevel: selectedRate.servicelevel },
        amount: 1,
        currency: selectedRate.currency,
        total: Number(selectedRate.amount),
        shipmentObjectId: selectedRate.shipment,
        totalInsuranceAmount: Number(insuranceAmount) || 0,
        includedInsurancePrice: Number(selectedRate?.includedInsurancePrice) || 0,
        returnAmount: isReturnRequested ? 1 : 0,
        signatureConfirmation,
        numberWithInsurance: Number(insuranceAmount) > 0 ? 1 : 0,
        numberWithSignature: signatureConfirmation?.hasSignatureConfirmation ? 1 : 0,
      },
    ] as RateTotal[];
  }, []);
}

function getRateListTotal(rateList: (RateTotal | ReturnRate)[]): number {
  const rates = rateList.filter((rate) => rate.hasOwnProperty('rate')) as RateTotal[];
  return rates.reduce((total, label) => total + label.total, 0);
}

function getRateListTotalText(rateList: (RateTotal | ReturnRate)[]): string {
  const rates = rateList.filter((rate) => rate.hasOwnProperty('rate')) as RateTotal[];
  const ratesTotal = rates.reduce((total, label) => total + label.total, 0);
  const currencyIso = rates[0] ? rates[0].currency : currencies.USD.iso;
  const noValue = `${currencies[currencyIso].symbol}—`;

  return ratesTotal
    ? i18n.t('translations:currency', {
        value: {
          value: ratesTotal,
          currency_iso: currencyIso,
        },
      })
    : noValue;
}

function getRateListTotalTextNoInsurance(rateList: (RateTotal | ReturnRate)[]): string {
  const rates = rateList.filter((rate) => rate.hasOwnProperty('rate')) as RateTotal[];
  const ratesTotal = rates.reduce(
    (total, label) => total + (label.total - Number(label.includedInsurancePrice)),
    0,
  );
  const currencyIso = rates[0] ? rates[0].currency : currencies.USD.iso;
  const noValue = `${currencies[currencyIso].symbol}—`;

  return ratesTotal
    ? i18n.t('translations:currency', {
        value: {
          value: ratesTotal,
          currency_iso: currencyIso,
        },
      })
    : noValue;
}

type RateSortOption = 'cheapest' | 'expensive' | 'fastest' | 'slowest';
// API Usage:
// rates.sort((a, b) => sortBy(a, b, 'cheapest'));
function sortBy(a: Rate, b: Rate, option: RateSortOption) {
  switch (option) {
    case 'cheapest':
      return parseFloat(a.amount) - parseFloat(b.amount);
    case 'expensive':
      return parseFloat(b.amount) - parseFloat(a.amount);
    case 'fastest':
      return a.estimatedDays - b.estimatedDays;
    case 'slowest':
      return b.estimatedDays - a.estimatedDays;
    default:
      return 0;
  }
}

const untrackableServiceLevels = [
  'First Class Package International Service',
  'CanadaPost Small Packets',
];

function isUntrackableRateServiceLevel(rate: Rate): boolean {
  return !!rate.servicelevel.name && untrackableServiceLevels.includes(rate.servicelevel.name);
}

type JSONRateTransactionSearch = {
  // string representation of a number
  amount: string;
  // string representation of a number
  amount_insurance_fee: string;
  // string representation of a number
  amount_local: string;
  // a UUID
  carrier_account: string;
  currency: keyof typeof currencies;
  currency_local: keyof typeof currencies;
  // TODO: unknown
  discount_group_name?: null;
  // string representation of a number
  insurance_amount: string;
  insurance_currency: keyof typeof currencies | null;
  object_id: string;
  provider: string;
  servicelevel_name: string;
  servicelevel_token: string;
};

type RateTransactionSearch = {
  // string representation of a number
  amount: string;
  // string representation of a number
  amountInsuranceFee: string;
  // string representation of a number
  amountLocal: string;
  // a UUID
  carrierAccount: string;
  currency: keyof typeof currencies;
  currencyLocal: keyof typeof currencies;
  // TODO: unknown
  discountGroupName?: null;
  // string representation of a number
  insuranceAmount: string;
  insuranceCurrency: keyof typeof currencies | null;
  objectId: string;
  provider: string;
  servicelevelName: string;
  servicelevelToken: string;
};

function transactionSearchRatefromJSON(jsonRate: JSONRateTransactionSearch): RateTransactionSearch {
  return {
    // string representation of a number
    amount: jsonRate.amount,
    // string representation of a number
    amountInsuranceFee: jsonRate.amount_insurance_fee,
    // string representation of a number
    amountLocal: jsonRate.amount_local,
    // a UUID
    carrierAccount: jsonRate.carrier_account,
    currency: jsonRate.currency,
    currencyLocal: jsonRate.currency_local,
    // TODO: unknown
    discountGroupName: jsonRate.discount_group_name,
    // string representation of a number
    insuranceAmount: jsonRate.insurance_amount,
    insuranceCurrency: jsonRate.insurance_currency || null,
    objectId: jsonRate.object_id,
    provider: jsonRate.provider,
    servicelevelName: jsonRate.servicelevel_name,
    servicelevelToken: jsonRate.servicelevel_token,
  };
}

export default Rate;
export {
  BaseRate,
  JSONBaseRate,
  fromJSONBaseRate,
  JSONRate,
  fromJSON,
  ServiceLevel,
  RateAttribute,
  getLabel,
  getLabelWithOneRate,
  optionFromRate,
  createRateList,
  getRateListTotal,
  getRateListTotalText,
  getRateListTotalTextNoInsurance,
  RateTotal,
  sortBy,
  isUntrackableRateServiceLevel,
  getRatesByAttribute,
  getRatesWithAttributeInDisplayOrder,
  getRatesForServiceLevelToken,
  transactionSearchRatefromJSON,
  JSONRateTransactionSearch,
  RateTransactionSearch,
};
